import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import type { Theme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useMatch } from 'react-router-dom';

import CustomNavLink from './components/CustomNavLink';
import Home from './components/NavItems/Home';
import logo from '../../assets/logo-codeword.jpg';
import Profile from './components/NavItems/Profile';
import Upload from './components/NavItems/Upload';
import Onboarding from './components/NavItems/Onboarding';
import Admin from './components/NavItems/Admin';
import Review from './components/NavItems/Review';
import Modifications from './components/NavItems/Modifications';
import { MODIFICATION_ENABLED } from '../../api/modification/config';
import { ADMIN_VIDEOS_ENABLED } from '../../api/adminVideos/config';
import { modificationRoutes } from '../../constants/routes.constants';
import { ADMIN_PATH } from '../../pages/Admin/adminStrings';
import { LOGIN, PAGE_NAME, SIGN_UP } from '../../constants/strings.constant';
import { UPLOAD_FEATURE_ENABLED } from '../../api/vpm/config';
import { ONBOARDING_FEATURE_ENABLED } from '../../api/onboarding/config';
import { baseRoutes } from '../../constants/routes.constants';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import type { Permission } from '../../constants/rbac';
import type { RootState } from '../../store';

// One style for both signed-out pills: today's pink pill, with the same hover and focus ring. Two at today's size do not
// fit a phone (measured in Edge, ops/header-signin-plan-2026-10-30.md), so the sides shrink below 900px, the text and the
// sides shrink again below 600px, and the height stays 2.5rem at every width. The words are the page switch's two constants with its capitalize
// (ToggleAuth.tsx), so they cannot drift from it: 'Sign up' reads "Sign Up" (Joey, 2026-10-30).
const authPillSx = (theme: Theme) => ({
  backgroundColor: 'primary.main',
  borderRadius: '999px',
  color: 'primary.contrastText',
  fontSize: { xs: '14px', sm: '16px' },
  fontWeight: 500,
  height: '2.5rem',
  // MUI's 64px minimum would hold "Login" 11px wider than its words at phone width, and the logo then touches the pill.
  minWidth: 0,
  px: { xs: 1, sm: 2.5, md: 3.5 },
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
  '&:hover, &:focus-visible': {
    backgroundColor: 'primary.main',
    boxShadow: `0 0 0 4px ${theme.palette.hoverRing}`,
  },
});

const Header = () => {
  const { hasPermission, canWrite, getAccessLevel, isReady, tenantId } = usePermissions();
  // A link is shown only to someone who can use the page behind it (28 Oct, the sign-in build, item 6). The gateway
  // refuses (403) every call on a permission the role holds only for its own company ('own') when the account has no
  // company (no tenantId), and RequirePermission shows the waiting page there instead of the page. So such a link
  // led only to the waiting page; it is hidden, by that same rule. Nothing changes for anyone with a company, nor
  // for a role that holds the permission at 'full' or 'view'. The Admin link needs nothing more: each of its checks
  // passes only at 'full' (see its comment). Home and Profile stay for everyone signed in, as on pmy/main.
  const canUse = (permission: Permission) =>
    hasPermission(permission) && !(getAccessLevel(permission) === 'own' && !tenantId);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const navigate = useNavigate();
  // useMatch, not a string compare: /sign-in/, /SIGN-IN and /Sign-Up still reach the forms, and count as them here.
  const onSignIn = useMatch(baseRoutes.signIn);
  const onSignUp = useMatch(baseRoutes.signUp);
  const showAuthPills = !isAuthenticated && isReady && !onSignIn && !onSignUp;
  
  const goToHome = () => {
    navigate('/');
  }

  return (
    // Side margins: 16px on phones (below 600px), 60px from tablets up as
    // before. On a phone the 60px left too little room for the logo and the
    // signed-out pills side by side (Joey, 2026-10-19).
    <Box sx={{ flexGrow: 1, marginLeft: { xs: 2, sm: '3.75rem' }, marginRight: { xs: 2, sm: '3.75rem' } }}>
      <AppBar position="static" color="transparent" elevation={0}>
        {/* RJ's revised homepage (20 Oct, Joey's Q4): a flat header on the
            page, with a thin rule in the soft warm border under it, as the
            mockup draws it. It used to be a white card. */}
        <Toolbar
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
          })}
        >
          <Box 
            onClick={goToHome}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              cursor: 'pointer'
            }}>
            {/* The client's horizontal lockup: the pink C and CODEWORD, cut
                from "Glossy Magenta Keyhole Logo and CODEWORD.jpg" with the
                whole of its glow, and stored at three times its larger size
                here. One picture, so it shows at every width.

                It sits inside the button the star sat in. That button is how
                the keyboard gets to "the logo goes home": it takes focus, and
                Enter or Space on it is a click, which the box above hears.
                Below 900px the nav is hidden and this is the header's only
                control. (HeaderLogo.test.tsx) */}
            <IconButton
              size="small"
              edge="start"
              color="inherit"
              sx={{ mr: 0, padding: 0, borderRadius: '0.5rem' }}
            >
              <Box
                component="img"
                src={logo}
                alt={PAGE_NAME}
                sx={{
                  display: 'block',
                  height: { xs: 46, sm: 61 },
                  width: 'auto',
                  // The logo is the client's JPEG, on a near-white ground.
                  // On the flat header that ground showed as a white box;
                  // blending it lets the page show through the white while
                  // the pink and the black keep their colour. The file
                  // itself is unchanged (Joey, 2026-10-19).
                  mixBlendMode: 'multiply',
                  // 36px below 360px, only while the two pills show, so they
                  // fit beside it (Joey, 2026-10-30). Added only then: every
                  // other header keeps this sx, and its class, exactly.
                  ...(showAuthPills && { '@media (max-width:359.95px)': { height: 36 } }),
                }}
              />
            </IconButton>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {/* RJ's revised homepage (20 Oct, Joey's Q4), and Joey, 2026-10-30.
              Signed out: two pink pills, Login to /sign-in and Sign Up to
              /sign-up. Not on those two pages, whose own switch does the same
              job; the header then has no control but the logo. The pills sit
              outside the nav box below, which is hidden under 900px, so a
              phone can still sign in. Signed in: today's icons and today's
              gates, exactly as before (the nav box, unchanged). The pills stay
              hidden until sign-in state is known (isReady), as the gated items
              are, so a signed-in visitor never sees them flash. */}
          {!isAuthenticated ? (showAuthPills && (
            <Box data-testid="header-auth-pills" sx={{ display: 'flex', gap: { xs: 0.5, sm: 1.5 } }}>
              <Button component={RouterLink} to={baseRoutes.signIn} variant="contained" disableElevation sx={authPillSx}>
                {LOGIN}
              </Button>
              <Button component={RouterLink} to={baseRoutes.signUp} variant="contained" disableElevation sx={authPillSx}>
                {SIGN_UP}
              </Button>
            </Box>
          )) : (
          <Box sx={{ display: { xs: 'none', md: 'flex' }, marginRight: '-25px' }}>
            <CustomNavLink to="/" NavItem={Home} />
            {/* RBAC: flag AND permission, both must pass - the flag decides
                whether the feature exists in this build, the role decides who
                sees the way in. Hidden while claims are unresolved. */}
            {ONBOARDING_FEATURE_ENABLED && canUse('licensor.profile.write') && (
              <CustomNavLink to={baseRoutes.licensorOnboarding} NavItem={Onboarding} />
            )}
            {UPLOAD_FEATURE_ENABLED && canUse('asset.upload') && (
              <CustomNavLink to={baseRoutes.upload} NavItem={Upload} />
            )}
            {/* The buyer's own AI modifications (Joey, 2026-10-30): flag AND
                submission.create at exactly 'own', with a company, as My
                profile's entry is. Never canUse: that would add Platform Admin
                ('full'), who does not submit. Desktop only, as Upload is: a
                phone buyer reaches it through My profile. */}
            {MODIFICATION_ENABLED && getAccessLevel('submission.create') === 'own' && !!tenantId && (
              <CustomNavLink to={modificationRoutes.mine} NavItem={Modifications} />
            )}
            {/* AI modifications to review (2026-10-20): flag AND submission.decide, so lic-admin and lic-approver only. */}
            {MODIFICATION_ENABLED && canUse('submission.decide') && (
              <CustomNavLink to={modificationRoutes.review} NavItem={Review} />
            )}
            {/* Admin area: shown to any role that holds at least one section.
                Absent, not disabled, for everyone else; hidden while claims are
                unresolved.

                MIRRORS sections.tsx AND MUST KEEP MIRRORING IT. Each clause is
                one section's gate. A section whose gate is an exact level is
                compared exactly here too, for the same reason: canWrite admits
                'own', and users.manage is 'own' for lic-admin and buyer-admin,
                who must not see the way in to a screen the server will refuse.

                orders.support.read is full-or-none, so canWrite is safe for it
                and agrees with the section's default gate.

                WHO THIS ADDS: nobody beyond Platform Admin. All four keys are
                'full' for cw-platform-admin and 'none' for every other role
                except users.manage, which is 'own' for lic-admin and
                buyer-admin and is compared exactly, so neither of them passes.

                IT USED TO ADD cw-platform-ops, because users.manage was 'full'
                for ops. That row predated the instruction that both admin
                features are Platform Admin only. Joseph Grafinger dropped the
                cell to 'none' on 2026-09-09, against the RBAC workbook, which
                is Vanja's sheet; she is being told as a record rather than
                asked. There is no discrepancy left for this comment to
                explain. */}
            {(canWrite('pricing.write') ||
              canWrite('pricing.defaults') ||
              canWrite('orders.support.read') ||
              getAccessLevel('users.manage') === 'full' ||
              // The AI-modification section (sections.tsx, flag on): exactly 'full', Platform Admin only.
              (MODIFICATION_ENABLED && getAccessLevel('submission.lock.reset') === 'full') ||
              (ADMIN_VIDEOS_ENABLED && getAccessLevel('catalog.manage') === 'full')) && (
              <CustomNavLink to={ADMIN_PATH} NavItem={Admin} />
            )}
            <CustomNavLink to="/profile" NavItem={Profile} />
          </Box>
          )}
          {/* The profile icon at phone width (28 Oct, Joey): the nav box above is hidden below 900px, and My profile is
              where every signed-in person finds Change password and Log out. Shown only below 900px, so the desktop
              header is unchanged. */}
          {isAuthenticated && (
            <Box data-testid="header-profile-phone" sx={{ display: { xs: 'flex', md: 'none' } }}>
              <CustomNavLink to="/profile" NavItem={Profile} />
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default Header;
